// ── Collection Status Classification ──────────────────────────────────────────
// Deterministic, single-status classification for every collection item.
// Each item gets exactly one status. No overlaps, no ambiguity.
//
// Statuses (in priority order — first match wins):
//   INCOMPLETE       — Missing required identity fields (name or house)
//   MISSING_CONC     — Has name+house but concentration is blank/unknown
//   COMPLETE         — Has name+house+valid concentration
//
// Future extension (requires catalog cross-ref):
//   CATALOG_MATCHED  — Complete AND matches a catalog entry
//   UNMATCHED        — Complete but no catalog match found
//   PLACEHOLDER      — User-created entry with record_origin = 'user_placeholder'

export const STATUS = {
  COMPLETE:      'complete',
  MISSING_CONC:  'missing_concentration',
  INCOMPLETE:    'incomplete',
}

// Human-readable labels for display
export const STATUS_LABELS = {
  [STATUS.COMPLETE]:     'Complete',
  [STATUS.MISSING_CONC]: 'Missing Concentration',
  [STATUS.INCOMPLETE]:   'Incomplete',
}

// Colors for badges (tailwind-compatible classes)
export const STATUS_COLORS = {
  [STATUS.COMPLETE]:     'text-emerald-400 border-emerald-400/30 bg-emerald-400/10',
  [STATUS.MISSING_CONC]: 'text-amber-400 border-amber-400/30 bg-amber-400/10',
  [STATUS.INCOMPLETE]:   'text-red-400 border-red-400/30 bg-red-400/10',
}

const BLANK_CONC = new Set(['', 'unknown', 'unspecified', 'n/a', 'none'])

// Classify a single collection item (a raw fragrance row, NOT a grouped card).
export function classifyItem(item) {
  const name = (item.name || '').trim()
  const house = (item.house || '').trim()
  const conc = (item.concentration || '').trim().toLowerCase()

  // Missing identity
  if (!name || !house) return STATUS.INCOMPLETE

  // Missing concentration
  if (BLANK_CONC.has(conc)) return STATUS.MISSING_CONC

  // Has all three identity fields
  return STATUS.COMPLETE
}

// Classify an entire collection and return summary metrics.
// Input: array of raw fragrance rows (not grouped).
// Returns: { items: Map<id, status>, counts: { complete, missing_concentration, incomplete }, rate: number }
export function classifyCollection(fragrances) {
  const items = new Map()
  const counts = { [STATUS.COMPLETE]: 0, [STATUS.MISSING_CONC]: 0, [STATUS.INCOMPLETE]: 0 }

  for (const f of fragrances) {
    const status = classifyItem(f)
    items.set(f.id, status)
    counts[status]++
  }

  const total = fragrances.length
  const rate = total > 0 ? (counts[STATUS.COMPLETE] / total * 100) : 0

  return { items, counts, total, completionRate: Math.round(rate * 10) / 10 }
}
